// ProposalDeleteModal.tsx — "Delete this category / template?" confirmation.
// Admin-only: it is only ever opened from the admin Proposals page.

import React from 'react';
import { Trash2, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog, { Notice } from './ProposalDialog';

interface ProposalDeleteModalProps {
  visible: boolean;
  /** "category" | "template" — only used in the wording */
  kind: 'category' | 'template';
  name?: string;
  loading?: boolean;
  /** server message when the delete was refused (e.g. category still has templates) */
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const ProposalDeleteModal: React.FC<ProposalDeleteModalProps> = ({ visible, kind, name, loading = false, error, onCancel, onConfirm }) => (
  <ProposalDialog
    visible={visible}
    onHide={onCancel}
    title={`Delete this ${kind}?`}
    subtitle={name ? <strong style={{ color: C.teal }}>{name}</strong> : 'This cannot be undone'}
    icon={Trash2}
    tone="danger"
    busy={loading}
    maxWidth="440px"
    footer={
      <>
        <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onCancel} disabled={loading} />
        <GlobalButton label={loading ? 'DELETING…' : 'DELETE'} variant="danger" icon={Trash2} onClick={onConfirm} loading={loading} />
      </>
    }
  >
    {error && <Notice tone="error">{error}</Notice>}
    <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub, lineHeight: 1.6 }}>
      {kind === 'category'
        ? 'The category is removed. A category that still has templates cannot be deleted — move or delete those templates first.'
        : 'The template is removed. Proposals already sent keep their own copy of the message and attachments, so nothing changes for them.'}
    </p>
  </ProposalDialog>
);

export default ProposalDeleteModal;
